// /agents/{name}: one agent in the agent builder (RFC-060 model 2,
// byj-1wv.115.12), made by a person who does not code and kept as it is made,
// so they leave and come back. Everything is done on their machine, through
// its workbench; what the main server keeps is what the list shows while it
// is off.
//
// What must come first is asked in order, on a page of its own: the model,
// then what the agent is for, which the builder agent drafts it from. Once it
// is drafted the page is where it is worked on (byj-1wv.115.48): what it is
// beside a chat with it — what it is told, what it may do, its files and the
// builder asked to change it on one side, trying it and its examples on the
// other — under a list of what to see to before it is shared, which says the
// order without holding anyone to it; sharing is a tab of its own, and nothing
// is shared until its owner has seen what callers can make it do and set its
// caps.
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { api, type AgentCap, type AgentExchangeView, type AgentUse, type BuiltAgent, type ShareApp, type ShareAppToken } from "../api";
import { AgentUsePanel, ExchangeList, type AgentUseApi } from "../components/AgentUse";
import { agentStanding, agentState, callCode, changeLine, deleteAgent, exampleId, notSignedIn, procedureSteps, useAgentThread, useBench, useMachine, type AbilityRow, type BuildInfo, type ModelState, type TestResult } from "../builder";
import { navigate } from "../router";
import { Ask, SignInNeeded, ThreadView } from "../components/BuilderThread";
import { Markdown } from "../markdown";
import { Codes, RpcError, type Entry } from "../workbench";
import type { SocketLike } from "../live";
import { CommandLine } from "../components/CommandLine";
import { useConfirm } from "../components/Confirm";
import { CopyButton } from "../components/Copy";
import { Drawer } from "../components/Drawer";
import { FormDialog } from "../components/FormDialog";
import { RowMenu } from "../components/RowMenu";
import { EmptyRows } from "../components/Rows";
import { StateBar } from "../components/StateBar";
import { Step, Tips } from "../components/Steps";
import { Link } from "../components/Nav";
import { Page, PageHead, PageLead, PageLoading, PageNotice } from "../components/Page";
import { panelId, tabId, Tabs } from "../components/Tabs";
import { phrases, t, whenMinute } from "../i18n";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/**
 * Every example it has passed when it was last run: an example added since —
 * by the builder, or from a caller's question — has not been run, so it is
 * not done until it is (byj-1wv.115.48).
 */
function allPassed(info: BuildInfo): boolean {
  const last = info.record["last-test"];
  return !!last && info.examples.length > 0 && info.examples.every((x) => last.examples.some((e) => e.id === x.id && e["passed?"]));
}

/** A field's sample words said as a sample, never read as what is to be typed (byj-1wv.115.48). */
const forExample = (text: string) => t("agents:forExample", { text });

/** Once drafted, a tab for each thing it is about: what it is, whether it answers well, who uses it. */
type Tab = "agent" | "test" | "share";
const tabs: Tab[] = ["agent", "test", "share"];

/**
 * How often a share reopening as the machine starts is asked after, for how
 * long, and then how often once it is taken to be slow: still asked, since
 * the machine runs and it may yet answer.
 */
const reopenPollMs = 3000;
export const reopenWaitMs = 90_000;
export const reopenSlowPollMs = 30_000;
/** How long a sign-in opened in the machine's terminal is waited on: a window closed or a sign-in given up is not waited on for the page's life. */
export const signInWaitMs = 10 * 60_000;

/**
 * What the machine's Claude Code answers on: read once the workbench is
 * ready, and again every few seconds while a sign-in it opened is not done,
 * so the page notices it without a reload.
 */
function useModel(bench: ReturnType<typeof useBench>) {
  const [model, setModel] = useState<ModelState | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // counted each time it reads signed in where it read not, so the page
  // closes what a step's Sign in opened
  const [signedIn, setSignedIn] = useState(0);
  const { call } = bench;
  // each read numbered as it is asked: only the latest asked is taken, and
  // none asked before a key was kept
  const reads = useRef(0);
  const load = useCallback(async () => {
    const mine = ++reads.current;
    try {
      const m = await call<ModelState>("builder/model");
      if (mine !== reads.current) return;
      setModel(m);
      setProblem(null);
    } catch (err) {
      if (mine === reads.current) setProblem(errText(err));
    }
  }, [call]);
  useEffect(() => {
    if (bench.ready) void load();
  }, [bench.ready, load]);
  // read again while a sign-in is open, for as long as one takes: a window
  // closed or a sign-in given up is not waited on for the page's life
  useEffect(() => {
    if (!waiting) return;
    const started = Date.now();
    const id = setInterval(() => {
      if (Date.now() - started >= signInWaitMs) setWaiting(false);
      else void load();
    }, 3000);
    return () => clearInterval(id);
  }, [waiting, load]);
  // done once it reads signed in where it read not: on a Mac the machine
  // cannot read Claude Code's keychain and always reads a subscription, so a
  // read alone never says the sign-in opened is done (agent.Credential)
  const was = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!model) return;
    if (was.current === "none" && model.credential !== "none") {
      setWaiting(false);
      setSignedIn((n) => n + 1);
    }
    was.current = model.credential;
  }, [model]);
  // the sign-in being opened: Sign in is held only that long, and pressed
  // again opens it again, the window closed or the sign-in given up
  const [opening, setOpening] = useState(false);
  const signIn = async () => {
    setProblem(null);
    setOpening(true);
    try {
      await call("builder/login");
      setWaiting(true);
    } catch (err) {
      setProblem(errText(err));
    } finally {
      setOpening(false);
    }
  };
  /** A key kept: what the machine answered, over any read still out, and no sign-in waited for. */
  const kept = (m: ModelState) => {
    reads.current += 1;
    setModel(m);
    setProblem(null);
    setWaiting(false);
  };
  return { model, kept, load, signIn, opening, waiting, stopWaiting: () => setWaiting(false), problem, signedIn };
}

/**
 * The model asked for first, above the steps, while it is not signed in:
 * Claude's sign-in, opened in the machine's terminal and linked there, or an
 * API key. Nothing can be drafted, tried or shared before it.
 */
function ModelFirst({ title, terminal, opening, waiting, problem, onSignIn, onKey, onDone }: {
  /** Its heading: the first of the steps before it is drafted, else what it asks. */
  title: string;
  terminal?: string;
  /** The sign-in being opened: Sign in is held this long alone. */
  opening: boolean;
  waiting: boolean;
  problem: string | null;
  onSignIn: () => void;
  onKey: () => void;
  /** Where a step's answer said it is not signed in though the machine reads it signed in: the person says when it is done. */
  onDone?: () => void;
}) {
  return (
    <section id="model-first" className="builder-card builder-ask" aria-label={title} data-testid="model-first">
      <div className="builder-card-head">
        <h3>{title}</h3>
      </div>
      <p className="flush">{t("agents:modelFirstLead")}</p>
      <div className="row wrap">
        <button className="primary" onClick={onSignIn} disabled={opening}>
          {t("agents:signIn")}
        </button>
        <button aria-haspopup="dialog" aria-controls="api-key-first" onClick={onKey}>
          {t("agents:addKey")}
        </button>
        {onDone && (
          <>
            <span className="grow" />
            <button onClick={onDone}>{t("agents:signedInDone")}</button>
          </>
        )}
      </div>
      {waiting && (
        <p className="muted flush" data-testid="sign-in-open">
          {t("agents:signInOpened")}{" "}
          {terminal && (
            <a href={`/s/${encodeURIComponent(terminal)}`} target="_blank" rel="noreferrer">
              {t("agents:openTerminal")}
            </a>
          )}{" "}
          {onDone ? t("agents:signInWaitingDone") : t("agents:signInWaiting")}
        </p>
      )}
      {problem && <p className="error">{t("common:error", { m: problem })}</p>}
    </section>
  );
}

/** What its abilities let the agent do, in words: on the Abilities step, and asked to be confirmed before it is shared. */
function ReachLine({ info }: { info: BuildInfo }) {
  // another service used with the owner's connection; writing only there
  // writes no file here, and is said as that alone (byj-1wv.115.42)
  const services = info.reach?.services?.join(", ") ?? "";
  const writesThere = !!info.reach?.["service-writes"]?.length;
  const onlyThere = info.reach?.level === "writes" && !info.reach.writes?.length && writesThere;
  // no reach is an agent byj cannot load yet — not drafted — never a guess at one (byj-1wv.115.29)
  if (!info.reach?.level) return <>{t("agents:notDrafted")}</>;
  return (
    <>
      {t("agents:reachNow")}{" "}
      <strong>
        {/* a shell held to reading programs still reads any file on the machine (byj-1wv.115.39) */}
        {info.reach.level === "reads" && info.reach["reads-anywhere"]
          ? t("agents:reach.readsAnywhere")
          : onlyThere
            ? t("agents:reach.servicesWrite", { s: services })
            : t(`agents:reach.${info.reach.level as "none" | "reads" | "hands-off" | "runs" | "writes" | "every-tool"}`)}
      </strong>
      {services && !onlyThere && (
        <span data-testid="reach-services">
          {" · "}
          {t(writesThere ? "agents:reach.servicesWrite" : "agents:reach.services", { s: services })}
        </span>
      )}
    </>
  );
}

/**
 * A part of the page in a frame of its own, so one part is not read into the
 * next: its title — with a count where it lists — and what acts on it at the
 * heading's end, then the part itself (byj-1wv.115.48).
 */
function Card({ id, title, count, tools, testid, children }: { id?: string; title: string; count?: ReactNode; tools?: ReactNode; testid?: string; children: ReactNode }) {
  return (
    <section id={id} className="builder-card" aria-label={title} data-testid={testid}>
      <div className="builder-card-head">
        <h3>
          {title}
          {count !== undefined && <span className="muted"> · {count}</span>}
        </h3>
        {tools && <span className="row-actions">{tools}</span>}
      </div>
      {children}
    </section>
  );
}

/**
 * A tab as a guide, as the Machines page says how a machine is used
 * (byj-1wv.115.48): its title and a line on what it is for, its steps down a
 * rail — each what to do, in words, a sentence on what it is, and the doing
 * of it — and what is good to know. A person who does not code reads what
 * each part is for where they do it, not from a name.
 */
function Guide({ testid, title, lead, tips, children }: { testid: string; title: string; lead: string; tips: string[]; children: ReactNode }) {
  return (
    <section className="card pad stack builder-guide" aria-label={title} data-testid={testid}>
      <h3 className="flush">{title}</h3>
      <p className="muted small flush">{lead}</p>
      <ol className="steps steps-rail">{children}</ol>
      <Tips title={t("agents:tipsTitle")} className="builder-tips">
        {tips.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </Tips>
    </section>
  );
}

/**
 * One step of a guide, as components/Steps draws a step: what to do, with
 * whether it is needed, worth doing or up to the person; a sentence on what
 * it is; what acts on it at the title's end; then the doing of it, and a
 * note under it.
 */
function BuilderStep({ id, testid, title, count, need, state, done = false, text, tools, note, children }: {
  id?: string;
  testid?: string;
  /** How it stands when that is neither done nor to do (a tag, after its need if it has one, before its tools) (byj-1wv.115.56). */
  state?: ReactNode;
  title: string;
  count?: ReactNode;
  need?: Need;
  /** What it asks is so (an example run with every one passing, say): its number a ✓, as the list above marks it. */
  done?: boolean;
  text: ReactNode;
  tools?: ReactNode;
  note?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Step
      id={id}
      testid={testid}
      className="builder-step"
      done={done}
      title={
        <>
          {title}
          {count !== undefined && <span className="muted"> · {count}</span>}
        </>
      }
      extra={
        <>
          {need && <span className={`builder-need is-${need}`}>{t(`agents:need.${need}`)}</span>}
          {state}
          {tools && <span className="row-actions">{tools}</span>}
        </>
      }
      text={text}
      note={note}
    >
      {children}
    </Step>
  );
}

/** Whether a thing on the list is needed to share it, worth doing, or up to the person. */
type Need = "required" | "recommended" | "optional";
type Check = { key: string; label: string; done: boolean; need: Need; onGo: () => void };

/**
 * What to see to before it is shared: each marked when it is, a press away
 * from where it is done, and said needed, worth doing or up to the person —
 * in no order, so no number: one seen scrolling back up is not a step
 * missed. Nothing in it holds the page back; it goes once the agent is shared.
 */
function Checklist({ items }: { items: Check[] }) {
  return (
    <section className="builder-checklist" aria-label={t("agents:checkTitle")} data-testid="checklist">
      <div className="builder-card-head">
        <h3>{t("agents:checkTitle")}</h3>
      </div>
      <p className="muted small flush">{t("agents:checkLead")}</p>
      <ul className="builder-checks">
        {items.map((x) => (
          <li key={x.key} className={x.done ? "is-done" : undefined} data-testid={`check-${x.key}`}>
            <span className="builder-check" aria-hidden="true">
              {x.done ? "✓" : ""}
            </span>
            <button className="link" onClick={x.onGo}>
              {x.label}
            </button>
            <span className={`builder-need is-${x.need}`}>{t(`agents:need.${x.need}`)}</span>
            {x.done && <span className="sr-only">{t("common:stepDone")}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The bundled abilities, said in the page's language (byj-1wv.115.35); a project's own, as its catalogue says it. */
const bundledAbilities = ["read-knowledge", "keep-notes", "run-python", "github-issues"] as const;
function abilityWords(a: { id: string; name: string; says: string }): { name: string; says: string } {
  const id = bundledAbilities.find((b) => b === a.id);
  return id ? { name: t(`agents:ability.${id}.name`), says: t(`agents:ability.${id}.says`) } : { name: a.name, says: a.says };
}

/** What a bundled ability needs to connect, in the page's language; a project's own as its catalogue says it (byj-1wv.115.44). */
function needWords(ability: string, n: { var: string; name: string; says: string }): { name: string; says: string } {
  return ability === "github-issues" && n.var === "GITHUB_PERSONAL_ACCESS_TOKEN"
    ? { name: t("agents:ability.github-issues.needs.name"), says: t("agents:ability.github-issues.needs.says") }
    : { name: n.name, says: n.says };
}


/**
 * The builder agent, asked in the person's words: before the agent is
 * drafted, what it is for, which it is drafted from; once it is, a change to
 * it — the draft read and only what is asked changed, never started again
 * from the words, which say what to change, not what the agent is (the
 * builder's prompt, "A change to an agent already drafted"). Not a step of
 * the guide: nothing waits on it. `waitFor`, why it cannot be asked yet,
 * said in its place.
 */
function Builder({ info, th, bench, onSignIn, title, waitFor }: {
  info: BuildInfo;
  th: ReturnType<typeof useAgentThread>;
  bench: ReturnType<typeof useBench>;
  onSignIn: () => void;
  title: string;
  waitFor?: string;
}) {
  const drafted = !!info.spec.description;
  return (
    <Card id="agent-builder" title={title} testid="describe-builder">
      <p className="muted small flush">{drafted ? t("agents:changeLead") : t("agents:describeLead")}</p>
      <ThreadView thread={th.thread} onChoose={th.choose} onSignIn={onSignIn} />
      {waitFor && <p className="muted small flush" data-testid="builder-waits">{waitFor}</p>}
      {/* the agent's name first, as the builder agent is told to keep it; drafted, said a change */}
      <Ask
        label={drafted ? t("agents:requestChange") : t("agents:draft")}
        placeholder={forExample(drafted ? t("agents:changePlaceholder") : t("agents:describePlaceholder"))}
        busy={th.thread.busy}
        disabled={!bench.ready || !!waitFor}
        // drafted, a change is a line or two: the field no taller than that
        rows={drafted ? 2 : 4}
        onAsk={(text) => th.prompt(drafted ? `agent: ${info.name}\n${changeLine}\n\n${text}` : `agent: ${info.name}\n\n${text}`)}
      />
    </Card>
  );
}

/** What the agent is told, as drafted: its first lines, all of it on a press, with how to change it (editing it by hand is byj-1wv.115.50). */
function InstructionStep({ info }: { info: BuildInfo }) {
  const [all, setAll] = useState(false);
  return (
    <BuilderStep
      id="agent-instruction-step"
      done={!!info.instruction}
      testid="agent-instruction"
      title={t("agents:step.instruction")}
      text={t("agents:stepText.instruction")}
      tools={
        info.instruction && (
          <button className="small" aria-expanded={all} aria-controls="agent-instruction-text" onClick={() => setAll(!all)}>
            {all ? t("agents:fold") : t("agents:unfold")}
          </button>
        )
      }
      note={info.instruction ? t("agents:instructionHow") : undefined}
    >
      {info.instruction ? (
        <pre id="agent-instruction-text" className={all ? "builder-instruction" : "builder-instruction builder-clamp"}>
          {info.instruction}
        </pre>
      ) : (
        <EmptyRows text={t("agents:instructionNone")} />
      )}
    </BuilderStep>
  );
}

/**
 * What the agent may do, and the ways of working the builder wrote: two
 * steps, each a set of choices side by side, each tick saved as it is made
 * (byj-1wv.115.48) — a person who does not code does not look for a Save,
 * and one left unpressed lost what they ticked. A tick shows at once; the
 * saves go one after another, the last made being what stands; one that
 * fails puts back what was saved and says why, a later one that does not
 * saying so in its place. Procedures are how it works, not what it may do,
 * so a step of their own.
 */
type Part = "abilities" | "procedures";

function Abilities({ info, bench, onInfo }: { info: BuildInfo; bench: ReturnType<typeof useBench>; onInfo: (i: BuildInfo) => void }) {
  const procedures = info.procedures ?? [];
  const saved = {
    abilities: new Set(info.abilities.filter((a) => a["ticked?"]).map((a) => a.id)),
    procedures: new Set(procedures.filter((p) => p["ticked?"]).map((p) => p.name)),
  };
  // ticks made and not yet answered: shown over what is saved
  const [pending, setPending] = useState<{ abilities: Set<string>; procedures: Set<string> } | null>(null);
  // how each step's ticks stand, said under that step alone: a tick of one does not say the other saved
  const [standing, setStanding] = useState<Record<Part, { saving: "idle" | "saving" | "saved"; problem: string | null }>>({
    abilities: { saving: "idle", problem: null },
    procedures: { saving: "idle", problem: null },
  });
  const chain = useRef<Promise<void>>(Promise.resolve());
  const lastOf = useRef<Record<Part, Promise<void> | null>>({ abilities: null, procedures: null });
  const shown = pending ?? saved;
  const stand = (part: Part, s: { saving: "idle" | "saving" | "saved"; problem: string | null }) => setStanding((x) => ({ ...x, [part]: s }));
  const tick = (part: Part, key: string, on: boolean) => {
    const set = new Set(shown[part]);
    if (on) set.add(key);
    else set.delete(key);
    const next = { ...shown, [part]: set };
    setPending(next);
    stand(part, { saving: "saving", problem: null });
    const has = new Set(procedures.map((p) => p.name));
    const mine = (chain.current = chain.current.then(async () => {
      try {
        onInfo(await bench.call<BuildInfo>("builder/abilities", { name: info.name, abilities: [...next.abilities], procedures: [...next.procedures].filter((n) => has.has(n)) }));
        if (chain.current === mine) {
          // the last made is saved, and every tick shown with it: an earlier one's failure is not what stands
          setStanding((x) => ({
            abilities: x.abilities.saving === "saving" || part === "abilities" || x.abilities.problem ? { saving: "saved", problem: null } : x.abilities,
            procedures: x.procedures.saving === "saving" || part === "procedures" || x.procedures.problem ? { saving: "saved", problem: null } : x.procedures,
          }));
        } else if (lastOf.current[part] === mine) stand(part, { saving: "saved", problem: null });
      } catch (err) {
        stand(part, { saving: "idle", problem: errText(err) });
      } finally {
        // the last made answered: what is saved is what is shown
        if (chain.current === mine) setPending(null);
      }
    }));
    lastOf.current[part] = mine;
  };
  // said under each step: that a tick is kept as it is made, and that it was
  const status = (part: Part, testid: string) => {
    const { saving, problem } = standing[part];
    return (
      <p className={problem ? "error small flush" : "muted small flush"} data-testid={testid} aria-live="polite">
        {problem ? t("common:error", { m: problem }) : saving === "saving" ? t("agents:ticksSaving") : saving === "saved" ? t("agents:ticksSaved") : t("agents:ticksKept")}
      </p>
    );
  };
  const chosen = info.abilities.filter((a) => a["ticked?"]);
  const ways = procedures.filter((p) => p["ticked?"]);
  return (
    <>
      <BuilderStep id="agent-abilities" testid="abilities-card" title={t("agents:step.abilities")} count={`${chosen.length}/${info.abilities.length}`} need="optional" done={chosen.length > 0} text={t("agents:stepText.abilities")}>
        <p className="builder-reach small flush" data-testid="reach">
          <ReachLine info={info} />
        </p>
        {/* each a choice, side by side where there is room, as a step's choices are on the Machines page */}
        <ul className="step-choices builder-choices" data-testid="abilities">
          {info.abilities.map((a) => (
            <li key={a.id} className={shown.abilities.has(a.id) ? "step-choice is-chosen" : "step-choice"}>
              <label className="toggle">
                <input type="checkbox" checked={shown.abilities.has(a.id)} disabled={!bench.ready} onChange={(e) => tick("abilities", a.id, e.target.checked)} />
                <strong className="small">{abilityWords(a).name}</strong>
              </label>
              <span className="muted small">{abilityWords(a).says}</span>
              {/* what the builder agent proposed, with its reason: a suggestion, ticked by no one but the person */}
              {info.record.proposed?.[a.id] && (
                <span className="small" data-testid="proposed">
                  <span className="badge">{t("agents:suggested")}</span> {info.record.proposed[a.id]}
                </span>
              )}
              {/* one that needs a connection says so, the connection itself under the choices */}
              {a.needs?.some((n) => !n["set?"]) && <span className="chip chip-warn builder-needs">{t("agents:needsConnection")}</span>}
            </li>
          ))}
        </ul>
        {/* what an ability needs to connect, under the choices rather than in one of them, so the choices
            stand alike; kept on the machine's home and never said back (byj-1wv.115.44) */}
        {info.abilities.some((a) => a.needs?.length) && (
          <div className="builder-connections stack" data-testid="connections">
            {info.abilities.flatMap((a) =>
              (a.needs ?? []).map((n) => (
                <div key={`${a.id}-${n.var}`} className="builder-connection-row">
                  <strong className="small">{abilityWords(a).name}</strong>
                  <Connection agent={info.name} need={{ ...n, ...needWords(a.id, n) }} bench={bench} onInfo={onInfo} />
                </div>
              )),
            )}
          </div>
        )}
        {status("abilities", "abilities-saved")}
      </BuilderStep>
      {/* the ways of working the builder wrote, each the agent's only when ticked (byj-1wv.115.43) */}
      {procedures.length > 0 && (
        <BuilderStep id="agent-procedures" testid="procedures-card" title={t("agents:step.procedures")} count={`${ways.length}/${procedures.length}`} need="optional" done={ways.length > 0} text={t("agents:stepText.procedures")}>
          <ul className="step-choices builder-choices" data-testid="procedures">
            {procedures.map((p) => (
              <li key={p.name} className={shown.procedures.has(p.name) ? "step-choice is-chosen" : "step-choice"}>
                <label className="toggle">
                  <input type="checkbox" checked={shown.procedures.has(p.name)} disabled={!bench.ready} onChange={(e) => tick("procedures", p.name, e.target.checked)} />
                  <strong className="small">{p.name}</strong>
                </label>
                <span className="muted small">{p.description}</span>
                <details>
                  <summary className="small">{t("agents:procedureSteps")}</summary>
                  <pre className="builder-instruction">{procedureSteps(p.content)}</pre>
                </details>
              </li>
            ))}
          </ul>
          {status("procedures", "procedures-saved")}
        </BuilderStep>
      )}
    </>
  );
}

/** One thing an ability needs to connect — a token its service takes — set on the machine, never shown again (byj-1wv.115.44). */
function Connection({ agent, need, bench, onInfo }: { agent: string; need: NonNullable<AbilityRow["needs"]>[number]; bench: ReturnType<typeof useBench>; onInfo: (i: BuildInfo) => void }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const send = async (v: string) => {
    setBusy(true);
    setProblem(null);
    try {
      onInfo(await bench.call<BuildInfo>("builder/connect", { name: agent, var: need.var, value: v }));
      setValue("");
    } catch (err) {
      setProblem(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="small builder-connection" data-testid="connection">
      {need["set?"] ? (
        <div className="row">
          <span className="badge">{t("agents:connected", { name: need.name })}</span>
          <button className="link" disabled={busy || !bench.ready} onClick={() => void send("")}>
            {t("agents:disconnect")}
          </button>
        </div>
      ) : (
        <>
          <div>{t("agents:notConnected", { name: need.name })}</div>
          <div className="muted">{need.says}</div>
          <div className="row">
            <input type="password" autoComplete="off" aria-label={need.name} placeholder={t("agents:tokenPlaceholder")} value={value} onChange={(e) => setValue(e.target.value)} />
            <button disabled={busy || !bench.ready || !value.trim()} onClick={() => void send(value)}>
              {t("agents:connect")}
            </button>
          </div>
          {/* where it is kept, under the field rather than in it, where a narrow field cut it off */}
          <div className="muted">{t("agents:tokenKept")}</div>
        </>
      )}
      {problem && <div className="error">{t("common:error", { m: problem })}</div>}
    </div>
  );
}

/** A file's text when it is text, else its bytes as base64 (a PDF). */
async function fileBody(f: File): Promise<{ text: string } | { base64: string }> {
  const bytes = new Uint8Array(await f.arrayBuffer());
  if (!bytes.slice(0, 8000).includes(0)) {
    try {
      return { text: new TextDecoder("utf-8", { fatal: true }).decode(bytes) };
    } catch {}
  }
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return { base64: btoa(bin) };
}

/** What the Knowledge step takes: text, and the documents whose text the machine takes out. */
const knowledgeAccept = ".md,.txt,.csv,.json,.html,.pdf,.docx,.pptx,.xlsx,.hwpx,text/*";

/** The files it answers from; how many there are told to the page, whose list says whether any were given. */
function Knowledge({ info, bench }: { info: BuildInfo; bench: ReturnType<typeof useBench> }) {
  const [files, setFiles] = useState<Entry[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // what was taken out of each document given (byj-1wv.115.23)
  const [taken, setTaken] = useState<{ from: string; path: string; chars: number }[]>([]);
  const input = useRef<HTMLInputElement>(null);
  // files dragged over the step, which it takes when they are let go
  const [dragging, setDragging] = useState(false);
  const [ask, confirmDialog] = useConfirm();
  const { call } = bench;
  const load = useCallback(async () => {
    try {
      const r = await call<{ entries: Entry[] }>("fs/list", { path: `agents/${info.name}/knowledge` });
      setFiles(r.entries.filter((e) => e.kind === "file"));
    } catch (err) {
      setProblem(errText(err));
    }
  }, [call, info.name]);
  useEffect(() => {
    if (bench.ready) void load();
  }, [bench.ready, load]);
  const add = async (list: FileList | null) => {
    setProblem(null);
    setTaken([]);
    const picked = Array.from(list ?? []);
    // emptied, as the app's other pickers are: the same file picked again —
    // changed, or refused and put right — is given again, not ignored
    if (input.current) input.current.value = "";
    for (const f of picked) {
      try {
        const r = await bench.call<{ path: string; from?: string; chars?: number }>("builder/knowledge", { name: info.name, path: f.name, ...(await fileBody(f)) });
        if (r.from) setTaken((x) => [...x, { from: r.from!, path: r.path, chars: r.chars ?? 0 }]);
      } catch (err) {
        setProblem(errText(err));
      }
    }
    void load();
  };
  // taken out once asked: it is what the agent answers from, and gone from the machine
  const remove = async (file: string) => {
    if (!(await ask(t("agents:removeFileAsk", { file }), { action: t("agents:removeFile"), danger: true }))) return;
    setProblem(null);
    setTaken([]);
    try {
      await bench.call("builder/knowledge-remove", { name: info.name, path: file });
    } catch (err) {
      setProblem(errText(err));
    }
    void load();
  };
  return (
    <BuilderStep
      id="agent-knowledge"
      done={(files?.length ?? 0) > 0}
      testid="knowledge-card"
      title={t("agents:step.files")}
      count={files?.length}
      need="optional"
      text={t("agents:stepText.files")}
      tools={
        // the app's way to pick a file: a button for a hidden input
        <button className="small" disabled={!bench.ready} onClick={() => input.current?.click()}>
          {t("agents:addKnowledge")}
        </button>
      }
      note={t("agents:knowledgeFormats")}
    >
      <input ref={input} type="file" hidden multiple accept={knowledgeAccept} disabled={!bench.ready} aria-label={t("agents:addKnowledge")} onChange={(e) => void add(e.target.files)} />
      {/* files dropped on it are given as picked ones are (byj-1wv.115.48) */}
      <div
        className={dragging ? "builder-drop is-over" : "builder-drop"}
        data-testid="knowledge-drop"
        onDragOver={(e) => {
          if (!bench.ready || !e.dataTransfer.types.includes("Files")) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (bench.ready) void add(e.dataTransfer.files);
        }}
      >
      {files?.length === 0 ? (
        <EmptyRows text={t("agents:noKnowledgeDrop")} />
      ) : (
        <ul className="stack builder-files" data-testid="knowledge">
          {files?.map((f) => (
            <li key={f.path} className="row">
              <span className="grow">{f.name}</span>
              <span className="muted small">{f.size !== undefined && t("agents:bytes", { n: f.size })}</span>
              {/* a file given by mistake, or to be given again changed, taken out (byj-1wv.115.48) */}
              <button className="small" disabled={!bench.ready} onClick={() => void remove(f.name)}>
                {t("agents:removeFile")}
              </button>
            </li>
          ))}
        </ul>
      )}
      </div>
      {taken.length > 0 && (
        <ul className="stack muted small" data-testid="knowledge-taken">
          {taken.map((x) => (
            <li key={x.path}>{t("agents:taken", { from: x.from, file: x.path.split("/").pop(), n: x.chars })}</li>
          ))}
        </ul>
      )}
      {problem && <p className="error flush">{t("common:error", { m: problem })}</p>}
      {confirmDialog}
    </BuilderStep>
  );
}

/**
 * The questions its share's callers asked, where its owner kept them
 * (byj-1wv.48), each made one of its examples once the owner says what a
 * good answer says: one of some words, or a rubric a judge reads.
 */
function FromCallers({ share, bench, name, onInfo, client = api }: { share: string; bench: ReturnType<typeof useBench>; name: string; onInfo: (i: BuildInfo) => void; client?: AgentUseApi }) {
  const [use, setUse] = useState<AgentUse | null>(null);
  const [making, setMaking] = useState<AgentExchangeView | null>(null);
  const [id, setId] = useState("");
  const [question, setQuestion] = useState("");
  const [words, setWords] = useState("");
  const [rubric, setRubric] = useState("");
  useEffect(() => {
    client.agentUse(share).then(setUse, () => setUse(null));
  }, [client, share]);
  const kept = (use?.exchanges ?? []).filter((x) => x.question);
  if (!use) return null;
  const open = (x: AgentExchangeView) => {
    setMaking(x);
    setId(exampleId(x.question ?? ""));
    setQuestion(x.question ?? "");
    setWords("");
    setRubric("");
  };
  const any = words
    .split(",")
    .map((w) => w.trim())
    .filter(Boolean);
  return (
    <BuilderStep id="agent-from-callers" testid="from-callers" title={t("agents:step.fromCallers")} count={kept.length} need="optional" text={t("agents:stepText.fromCallers")}>
      {use.keeping ? (
        <p className="muted small">{t("agents:fromCallersLead")}</p>
      ) : (
        // where keeping is turned on: the share page's policy
        <p className="muted small">
          {t("agents:fromCallersOff")} <Link to={`/s/${encodeURIComponent(share)}`}>{t("agents:openSharePage")}</Link>
        </p>
      )}
      {kept.length > 0 && (
        <ExchangeList
          exchanges={kept}
          action={(x) => (
            <div className="row">
              <span className="grow" />
              <button className="small" aria-haspopup="dialog" disabled={!bench.ready} onClick={() => open(x)}>
                {t("agents:makeExample")}
              </button>
            </div>
          )}
        />
      )}
      {making && (
        <FormDialog
          id="example-from-caller"
          title={t("agents:makeExample")}
          submitLabel={t("agents:addExample")}
          canSubmit={/^[a-z0-9][a-z0-9-]*$/.test(id) && question.trim().length > 0 && (any.length > 0 || rubric.trim().length > 0)}
          onClose={() => setMaking(null)}
          onSubmit={async () => {
            onInfo(
              await bench.call<BuildInfo>("builder/example", {
                name,
                example: { id, question: question.trim(), ...(any.length ? { "contains-any": any } : {}), ...(rubric.trim() ? { rubric: rubric.trim() } : {}) },
              }),
            );
            setMaking(null);
          }}
        >
          <label className="field">
            {t("agents:exampleId")}
            <input value={id} onChange={(e) => setId(e.target.value)} aria-label={t("agents:exampleId")} />
          </label>
          <label className="field">
            {t("agents:exampleQuestion")}
            <textarea value={question} rows={3} onChange={(e) => setQuestion(e.target.value)} aria-label={t("agents:exampleQuestion")} />
          </label>
          {making.answer && (
            <details className="small">
              <summary>{t("agents:exampleItAnswered")}</summary>
              <p>{making.answer}</p>
            </details>
          )}
          <label className="field">
            {t("agents:exampleWords")}
            <input value={words} placeholder={forExample(t("agents:exampleWordsPlaceholder"))} onChange={(e) => setWords(e.target.value)} aria-label={t("agents:exampleWords")} />
          </label>
          <label className="field">
            {t("agents:exampleRubric")}
            <textarea value={rubric} rows={2} onChange={(e) => setRubric(e.target.value)} aria-label={t("agents:exampleRubric")} />
          </label>
        </FormDialog>
      )}
    </BuilderStep>
  );
}

/**
 * A chat with the agent as the people it is shared with will ask it: the
 * test guide's first step, its thread the page's, so going to another tab
 * and back loses nothing.
 */
function TryStep({ info, th, bench, onSignIn }: { info: BuildInfo; th: ReturnType<typeof useAgentThread>; bench: ReturnType<typeof useBench>; onSignIn: () => void }) {
  return (
    <BuilderStep id="agent-try" testid="try" done={th.thread.items.some((x) => x.kind === "agent")} title={t("agents:step.try")} text={t("agents:stepText.try")}>
      <ThreadView thread={th.thread} onChoose={th.choose} onSignIn={onSignIn} />
      {/* one of its own examples is the question it is asked first, if it has one */}
      <Ask label={t("agents:ask")} placeholder={forExample(info.examples[0]?.prompt ?? t("agents:askPlaceholder"))} busy={th.thread.busy} disabled={!bench.ready} onAsk={th.prompt} />
    </BuilderStep>
  );
}

/** Its examples run and their results kept with the main server, and the questions its callers asked made examples. */
function Examples({ info, bench, onInfo, onSignIn, share }: { info: BuildInfo; bench: ReturnType<typeof useBench>; onInfo: (i: BuildInfo) => void; onSignIn: () => void; share?: string }) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const last: TestResult | undefined = info.record["last-test"];
  const run = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const next = await bench.call<BuildInfo>("builder/test", { name: info.name });
      onInfo(next);
      const r = next.record["last-test"];
      if (r) await api.putAgent(info.name, { test: { passed: r.passed, of: r.of, at: r.at } });
    } catch (err) {
      setProblem(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <BuilderStep
        id="agent-examples"
        done={allPassed(info)}
        testid="examples-card"
        title={t("agents:step.examples")}
        count={info.examples.length}
        need="recommended"
        text={t("agents:examplesLead")}
        tools={
          <button className="primary small" disabled={busy || !bench.ready || info.examples.length === 0} onClick={() => void run()}>
            {busy ? t("agents:testing") : t("agents:runExamples")}
          </button>
        }
      >
        {last && (
          <p className="small flush" data-testid="last-test">
            {t("agents:lastTest", { passed: last.passed, of: last.of, w: whenMinute(new Date(last.at).toISOString()) })}
          </p>
        )}
        {info.examples.length === 0 && <EmptyRows text={t("agents:noExamples")} />}
        <ul className="builder-examples" data-testid="examples">
          {info.examples.map((x) => {
            const r = last?.examples.find((e) => e.id === x.id);
            return (
              <li key={x.id}>
                <div className="row">
                  <strong className="grow">{x.prompt}</strong>
                  {r && <span className={`chip ${r["passed?"] ? "chip-ok" : "chip-failed"}`} data-testid="example-result">{r["passed?"] ? t("agents:passed") : t("agents:failed")}</span>}
                </div>
                {x.files && x.files.length > 0 && (
                  <p className="muted small flush" data-testid="example-files">
                    {t("agents:startsWith", { files: x.files.join(", ") })}
                  </p>
                )}
                {r?.checks.filter((c) => !c["pass?"]).map((c) => (
                  <p key={c.name} className="muted small flush">
                    {c.detail}
                  </p>
                ))}
                {r?.answer &&
                  (notSignedIn(r.answer) ? (
                    <SignInNeeded onSignIn={onSignIn} />
                  ) : (
                    // an answer can run long: folded, so the examples stay a list
                    <details className="small">
                      <summary>{t("agents:seeAnswer")}</summary>
                      <div className="builder-answer">
                        <Markdown text={r.answer} />
                      </div>
                    </details>
                  ))}
                {r?.error && notSignedIn(r.error) && <SignInNeeded onSignIn={onSignIn} />}
              </li>
            );
          })}
        </ul>
        {problem && <p className="error flush">{t("common:error", { m: problem })}</p>}
      </BuilderStep>
      {share && <FromCallers share={share} bench={bench} name={info.name} onInfo={onInfo} />}
    </>
  );
}

function Apps({ share }: { share: string }) {
  const [apps, setApps] = useState<ShareApp[] | null>(null);
  const [made, setMade] = useState<ShareAppToken | null>(null);
  const [making, setMaking] = useState(false);
  const [name, setName] = useState("");
  // a list that could not be read is said so, never a promise left to fail unseen
  const [problem, setProblem] = useState<string | null>(null);
  // an application removed only once asked: its tokens stop at once (byj-1wv.115.49)
  const [ask, confirmDialog] = useConfirm();
  const load = useCallback(async () => {
    try {
      setApps((await api.shareApps(share)).apps);
      setProblem(null);
    } catch (err) {
      setProblem(errText(err));
    }
  }, [share]);
  useEffect(() => {
    void load();
  }, [load]);
  const code = made ? callCode(made.url) : null;
  return (
    <div className="stack" data-testid="apps">
      {problem && <p className="error flush">{t("common:error", { m: problem })}</p>}
      <ul className="stack">
        {apps?.length === 0 && <li className="muted">{t("agents:noApps")}</li>}
        {apps?.map((a) => (
          <li key={a.id} className="row">
            <span className="grow">
              {a.name} <span className="muted small">{a.last_used_at ? t("agents:appUsed", { w: whenMinute(a.last_used_at) }) : t("agents:appUnused")}</span>
            </span>
            <button className="small" onClick={async () => setMade(await api.shareAppToken(share, a.id))}>
              {t("agents:newToken")}
            </button>
            <button
              className="small"
              onClick={async () => {
                if (!(await ask(t("agents:removeAppAsk", { app: a.name }), { action: t("agents:removeApp"), danger: true }))) return;
                let failed: string | null = null;
                try {
                  await api.removeShareApp(share, a.id);
                } catch (err) {
                  failed = errText(err);
                }
                // the list read again, then why it was not removed: a read that succeeds clears what was said before it
                await load();
                if (failed) setProblem(failed);
              }}
            >
              {t("agents:removeApp")}
            </button>
          </li>
        ))}
      </ul>
      <div className="row">
        <span className="grow" />
        <button aria-controls="new-app" onClick={() => setMaking(true)}>
          {t("agents:newApp")}
        </button>
      </div>
      {made && code && (
        <div className="card pad stack" data-testid="app-token">
          <p>{t("agents:tokenOnce", { app: made.app.name, w: whenMinute(made.expires_at) })}</p>
          <CommandLine command={made.token} />
          <p className="muted small">{t("agents:endpoint")}</p>
          <CommandLine command={made.url} />
          {(["ts", "py", "curl"] as const).map((k) => (
            <div key={k} className="stack">
              <div className="row">
                <strong className="grow">{t(`agents:code.${k}`)}</strong>
                <CopyButton text={code[k]} />
              </div>
              <pre className="builder-code">{code[k]}</pre>
            </div>
          ))}
        </div>
      )}
      {making && (
        <FormDialog
          id="new-app"
          title={t("agents:newApp")}
          submitLabel={t("agents:make")}
          canSubmit={name.trim().length > 0}
          onClose={() => setMaking(false)}
          onSubmit={async () => {
            setMade(await api.newShareApp(share, name.trim()));
            setMaking(false);
            setName("");
            void load();
          }}
        >
          <label className="stack">
            <span>{t("agents:appName")}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={forExample(t("agents:appNamePlaceholder"))} autoFocus />
          </label>
        </FormDialog>
      )}
      {confirmDialog}
    </div>
  );
}

/**
 * Whether it is shared, and how: its share and the model it answers on —
 * which says whom it can answer — its caps, the share page where callers are
 * invited, and applications. Sharing asks first, in a dialog, with the caps
 * the machine says hold.
 */
function Share({ info, bench, row, model, modelProblem, stateWords, onKey, onInfo, onRow }: {
  info: BuildInfo;
  bench: ReturnType<typeof useBench>;
  row: BuiltAgent | undefined;
  model: ModelState | null;
  /** Why the model's state could not be read, said beside it. */
  modelProblem: string | null;
  /** How its share stands, as the bar says it: the page's one reckoning of it (byj-1wv.115.38). */
  stateWords: string;
  onKey: () => void;
  onInfo: () => Promise<void>;
  onRow: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // Share it asks first: what callers can make it do, and its caps
  const [confirming, setConfirming] = useState(false);
  const [messages, setMessages] = useState("");
  const [cost, setCost] = useState("");
  const [cap, setCap] = useState<AgentCap | null>(null);
  const caps = { messages: Number(messages), cost: Number(cost) };
  const capsRight = Number.isInteger(caps.messages) && caps.messages > 0 && Number.isFinite(caps.cost) && caps.cost > 0;
  const noModel = model?.credential === "none";
  // whether it can answer others, as the machine says it (shares_with_others): the
  // model step's ✓, its tag, inviting callers and applications all read this one
  // (byj-1wv.115.56)
  const answersOthers = !!model?.shares_with_others;
  // not read yet, or could not be: nothing is shared on a credential unknown;
  // nor before it is drafted, when the page has no Share to show
  const unknownModel = model === null;
  const act = async (f: () => Promise<void>) => {
    setBusy(true);
    setProblem(null);
    try {
      await f();
    } catch (err) {
      setProblem(errText(err));
    } finally {
      setBusy(false);
    }
  };
  const on = info.share.on === true;
  const share = async (p: { on: boolean; max_messages?: number; max_cost?: number }) => {
    try {
      const r = await bench.call<{ share: string; on: boolean }>("builder/share", { name: info.name, ...p });
      // whether it is shared, told the main server too: a share stopped on purpose and a machine off both end it (byj-1wv.115.49)
      await api.putAgent(info.name, { share_id: r.share, shared: r.on });
    } finally {
      // both read again whatever failed, so the page shows what the machine
      // did and the main server keeps, the machine first (byj-1wv.115.49)
      await onInfo();
      await onRow();
    }
  };
  const confirm = () => {
    // the caps it was last shared with, or byj-share's defaults for one never
    // shared: the machine says which (builder/state), the page keeps no copy
    setMessages(info.share.max_messages ? String(info.share.max_messages) : "");
    setCost(info.share.max_cost ? String(info.share.max_cost) : "");
    setConfirming(true);
    api.agentCap().then(setCap, () => setCap(null));
  };
  return (
    <>
      {/* the model it answers on says whom it can answer: a subscription its owner alone, a key anyone */}
      <BuilderStep
        id="agent-model"
        // done only once it can answer others: a model that answers its owner alone (a
        // subscription) is said as that, not as nothing left to do (byj-1wv.115.56)
        done={answersOthers}
        state={
          !!model && !noModel && !answersOthers && (
            <span className="chip chip-warn" data-testid="model-owner-only">
              {t("agents:modelOwnerOnly")}
            </span>
          )
        }
        testid="model-state"
        title={t("agents:step.model")}
        text={model?.credential === "key" ? t("agents:onKey") : model?.credential === "subscription" ? t("agents:onSubscription") : noModel ? t("agents:onNothing") : t("agents:modelReading")}
        tools={
          !noModel && (
            <button className="small" aria-haspopup="dialog" aria-controls="api-key-share" onClick={onKey}>
              {model?.credential === "key" ? t("agents:replaceKey") : t("agents:addKey")}
            </button>
          )
        }
      >
        {modelProblem && <p className="error flush">{t("common:error", { m: modelProblem })}</p>}
      </BuilderStep>
      <BuilderStep
        id="agent-share-state"
        done={on}
        testid="share-state"
        title={t("agents:step.share")}
        need="required"
        text={t("agents:stepText.share")}
        tools={
          <button
            className={on ? "small" : "primary small"}
            aria-haspopup={on ? undefined : "dialog"}
            disabled={busy || !bench.ready || (!on && (noModel || unknownModel))}
            onClick={() => (on ? void act(() => share({ on: false })) : confirm())}
            data-testid="share-toggle"
          >
            {on ? t("agents:stopSharing") : t("agents:startSharing")}
          </button>
        }
        note={t("agents:whileMachineRuns")}
      >
        {problem && <p className="error flush">{t("common:error", { m: problem })}</p>}
        <p className="flush builder-state">
          <strong>{stateWords}</strong>
        </p>
        {/* why it cannot be shared yet, beside the state */}
        {!on && noModel && (
          <p className="muted flush" data-testid="share-needs">
            {t("agents:shareNeedsModel")}
          </p>
        )}
        {on && (info.share.max_messages ?? 0) > 0 && (info.share.max_cost ?? 0) > 0 && (
          <p className="flush" data-testid="share-caps">
            {t("agents:capsNow", { m: info.share.max_messages, c: info.share.max_cost })}
          </p>
        )}
        {confirming && (
          <FormDialog
            id="share-confirm"
            testid="share-confirm"
            title={t("agents:confirmTitle", { name: info.name })}
            submitLabel={t("agents:startSharing")}
            canSubmit={capsRight}
            onClose={() => setConfirming(false)}
            onSubmit={async () => {
              await share({ on: true, max_messages: caps.messages, max_cost: caps.cost });
              setConfirming(false);
            }}
          >
            <p>{t("agents:confirmWho")}</p>
            {/* what their questions can make it do: its reach, as the agent's guide says it */}
            <p data-testid="confirm-reach">
              <ReachLine info={info} />
            </p>
            <p className="muted small">{t("agents:confirmChange")}</p>
            {model?.credential === "subscription" && <p className="muted small">{t("agents:confirmSubscription")}</p>}
            <h3>{t("agents:capsTitle")}</h3>
            <p className="muted small">{t("agents:capsLead")}</p>
            {/* a machine from before the caps were said gives none: asked for, not left a Share that cannot be pressed (byj-1wv.115.48) */}
            {!(info.share.max_messages && info.share.max_cost) && (
              <p className="small" data-testid="caps-unknown">
                {t("agents:capsUnknown")}
              </p>
            )}
            <div className="row wrap">
              <label className="stack">
                <span>{t("agents:capMessages")}</span>
                <input type="number" min={1} step={1} value={messages} onChange={(e) => setMessages(e.target.value)} />
              </label>
              <label className="stack">
                <span>{t("agents:capCost")}</span>
                <input type="number" min={0.01} step={0.01} value={cost} onChange={(e) => setCost(e.target.value)} />
              </label>
            </div>
            {/* the owner's month across all their agent shares, set on /shares */}
            {cap && (
              <p className="muted small" data-testid="month-cap">
                {phrases.agentCapLine(cap.spent_usd, cap.cap_usd, cap.set)} <Link to="/shares">{t("agents:monthCapWhere")}</Link>
              </p>
            )}
          </FormDialog>
        )}
      </BuilderStep>
      {/* who it is for, once shared: callers are invited on its share page */}
      <BuilderStep id="agent-invite" testid="share-invite" title={t("agents:step.invite")} text={t("agents:stepText.invite")}>
        {row?.share_id && on ? (
          <p className="flush" data-testid="share-page">
            {answersOthers && <span className="muted">{t("agents:inviteHow")} </span>}
            <Link to={`/s/${encodeURIComponent(row.share_id)}`}>{answersOthers ? t("agents:sharePage") : t("agents:sharePageOwner")}</Link>
          </p>
        ) : (
          <p className="muted small flush">{t("agents:inviteNeedsShare")}</p>
        )}
      </BuilderStep>
      <BuilderStep id="agent-apps" testid="applications" title={t("agents:step.apps")} need="optional" text={t("agents:stepText.apps")}>
        {row?.share_id && on && answersOthers ? <Apps share={row.share_id} /> : <p className="muted small flush">{t("agents:appsNeed")}</p>}
      </BuilderStep>
    </>
  );
}


/**
 * A copy of the agent for teammates to run on their own machines with their
 * own Claude: not this agent shared, so not on the Share tab, but in the
 * head's menu, over the page.
 */
function Teammates({ info, bench, model, open, onClose }: { info: BuildInfo; bench: ReturnType<typeof useBench>; model: ModelState | null; open: boolean; onClose: () => void }) {
  const [invite, setInvite] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const give = async () => {
    setBusy(true);
    setProblem(null);
    try {
      setInvite((await bench.call<{ invite: string }>("builder/publish", { name: info.name })).invite);
    } catch (err) {
      setProblem(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer id="agent-teammates" title={t("agents:teammatesTitle")} open={open} onClose={onClose}>
      <div className="stack" data-testid="teammates">
        <p className="muted flush">{t("agents:teammatesLead")}</p>
        <p className="flush">
          <strong>{t("agents:onTheirClaude")}</strong> {t("agents:onTheirClaudeSays")}
        </p>
        {invite ? (
          <>
            <CommandLine command={invite} />
            {/* changed since, it is sent again: what it is for and its files as they are now */}
            <div className="row">
              <button disabled={busy || !bench.ready} onClick={() => void give()} data-testid="give-again">
                {t("agents:giveAgain")}
              </button>
            </div>
          </>
        ) : (
          <div className="row">
            <button className="primary" disabled={busy || !bench.ready} onClick={() => void give()}>
              {t("agents:giveTeammates")}
            </button>
          </div>
        )}
        {problem && <p className="error flush">{t("common:error", { m: problem })}</p>}
        <p className="flush">
          <strong>{t("agents:onYourKey")}</strong> {model?.credential === "key" ? t("agents:onYourKeySays") : t("agents:onYourKeyNeeds")}
        </p>
      </div>
    </Drawer>
  );
}

/**
 * A built agent's page with its machine off (byj-1wv.115.49): what the main
 * server keeps of it — how its share stands and what it cost, how it was
 * used, and its applications, whose tokens are revoked here — and that the
 * rest (changing, testing, sharing it on or off, deleting it) waits for the
 * machine. Nothing here is read from the machine.
 */
function MachineOff({ name, row, problem, onRetry }: { name: string; row: BuiltAgent | undefined; problem: string | null; onRetry: () => void }) {
  const share = row?.share_id;
  const facts = row
    ? [row.cost_usd > 0 ? t("agents:cost", { usd: row.cost_usd.toFixed(2) }) : null, row.test ? t("agents:testSummary", { passed: row.test.passed, of: row.test.of }) : t("agents:notTested")].filter(Boolean)
    : [];
  return (
    <Page className="agent-page" data-testid="agent-off">
      <PageHead title={name} />
      {row && (
        <StateBar
          className="agent-bar"
          label={t("agents:barLabel")}
          state={agentState(row)}
          stateLabel={<span className="share-bar-state">{agentStanding(row)}</span>}
          actions={share && <Link to={`/s/${encodeURIComponent(share)}`}>{t("agents:openSharePage")}</Link>}
          testid="agent-bar"
        >
          <span className="share-bar-facts">{facts.join(" · ")}</span>
        </StateBar>
      )}
      <PageLead>
        <p data-testid="off-lead">
          {t("agents:offLead")} <Link to="/machines">{t("agents:startMachine")}</Link>
        </p>
      </PageLead>
      {!row && problem ? (
        <div className="row wrap" data-testid="off-problem">
          <p className="error grow flush">{t("common:error", { m: problem })}</p>
          <button className="small" onClick={onRetry}>
            {t("common:retry")}
          </button>
        </div>
      ) : !row ? (
        <p className="muted">{t("agents:offNotKept")}</p>
      ) : !share ? (
        <p className="muted" data-testid="off-never-shared">
          {t("agents:offNeverShared")}
        </p>
      ) : (
        <div className="stack">
          {/* the share page's own summary of its use, its questions in a drawer */}
          <AgentUsePanel shareId={share} turns={0} />
          <Card title={t("agents:appsTitle")} testid="off-apps">
            <Apps share={share} />
          </Card>
        </div>
      )}
    </Page>
  );
}

/** `socket` is a test's: the machine's workbench, as the page opens it. */
export function AgentPage({ name, socket }: { name: string; socket?: (url: string) => SocketLike }) {
  const [tab, setTab] = useState<Tab>("agent");
  // the tabs opened so far: each made the first time it is shown, then kept
  // (hidden while another is), so what was typed or opened on it is there on
  // coming back, and a tab never opened asks the machine nothing
  const [seen, setSeen] = useState<ReadonlySet<Tab>>(() => new Set<Tab>(["agent"]));
  useEffect(() => {
    setSeen((s) => (s.has(tab) ? s : new Set([...s, tab])));
  }, [tab]);
  const kept = (x: Tab) => seen.has(x) || tab === x;
  const [info, setInfo] = useState<BuildInfo | null>(null);
  const [row, setRow] = useState<BuiltAgent | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  // a copy for teammates, from the head's menu
  const [teammates, setTeammates] = useState(false);
  const { machine } = useMachine();
  const bench = useBench(machine?.share_id, socket);
  const model = useModel(bench);
  // both threads are the page's: what the builder said drafting the agent is
  // still there once the page turns from what comes first to the work, and
  // what the agent was asked is still there after another tab
  const builder = useAgentThread(bench, info?.cwd, "builder");
  const trying = useAgentThread(bench, info?.cwd, info?.name ?? "");
  // what the agent is — its spec, what it is told, the abilities and procedures ticked — byj takes as
  // a session opens, so once it changes Try's next question opens one again, what was said kept;
  // one being answered is let finish first
  const defined = info
    ? JSON.stringify([info.spec, info.instruction, info.abilities.filter((a) => a["ticked?"]).map((a) => a.id), (info.procedures ?? []).filter((p) => p["ticked?"]).map((p) => p.name)])
    : "";
  const tried = useRef(defined);
  const { renew } = trying;
  const tryingBusy = trying.thread.busy;
  useEffect(() => {
    if (tried.current === defined || tryingBusy) return;
    // the agent as it first loads is what the first session opens on: nothing changed yet
    const changed = tried.current !== "";
    tried.current = defined;
    if (changed) renew();
  }, [defined, tryingBusy, renew]);
  // the API key, asked for first and on Share alike: the dialog named for
  // the button that opened it, so closing it gives that one focus
  const [keying, setKeying] = useState<"first" | "share" | null>(null);
  const [key, setKey] = useState("");
  // a step gone to from the list: its tab shown, then scrolled to once it is there
  const [goTo, setGoTo] = useState<string | null>(null);
  useEffect(() => {
    if (!goTo) return;
    document.getElementById(goTo)?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    setGoTo(null);
  }, [goTo]);
  const go = (to: Tab, id?: string) => {
    setTab(to);
    if (id) setGoTo(id);
  };
  // a step's "Sign in" goes to the model asked for above the steps, read
  // again first, and shown whatever the read says: the step's answer is the
  // proof it is not signed in, which a Mac's read cannot see (agent.Credential)
  const [asked, setAsked] = useState(false);
  // counted at each press: every press goes there, not the first alone
  const [askedAt, setAskedAt] = useState(0);
  const toModel = () => {
    void model.load();
    setAsked(true);
    setAskedAt((n) => n + 1);
  };
  useEffect(() => {
    if (askedAt > 0) document.getElementById("model-first")?.scrollIntoView?.({ block: "start" });
  }, [askedAt]);
  // read signed in where it read not: what a step's Sign in opened is done
  useEffect(() => {
    if (model.signedIn > 0) setAsked(false);
  }, [model.signedIn]);
  // the Share tab reads the model again as it opens: a sign-in finished in
  // the terminal without the page's own button is seen there
  const { load: loadModel } = model;
  useEffect(() => {
    if (tab === "share" && bench.ready) void loadModel();
  }, [tab, bench.ready, loadModel]);
  // read once at least, so the page with its machine off says what the main server keeps, not a guess before it
  const [rowRead, setRowRead] = useState(false);
  // why it could not be read, said with a retry rather than taken for nothing kept (byj-1wv.115.49)
  const [rowProblem, setRowProblem] = useState<string | null>(null);
  const loadRow = useCallback(async () => {
    try {
      setRow((await api.agents()).agents.find((a) => a.name === name));
      setRowProblem(null);
    } catch (err) {
      setRowProblem(errText(err));
    } finally {
      setRowRead(true);
    }
  }, [name]);
  // Delete, from the head's menu with the machine running: its share stopped
  // and its project removed there, then forgotten here (byj-1wv.115.49)
  const [ask, confirmDialog] = useConfirm();
  const [deleteProblem, setDeleteProblem] = useState<string | null>(null);
  const remove = async () => {
    if (!(await ask(t("agents:deleteAsk", { name }), { action: t("agents:delete"), danger: true }))) return;
    setDeleteProblem(null);
    try {
      if (await deleteAgent(bench.call, name, row, () => ask(t("agents:forgetAsk", { name }), { action: t("agents:forget"), danger: true }))) navigate("/agents");
    } catch (err) {
      setDeleteProblem(errText(err));
      // a delete done on the machine and not yet forgotten is tried again as not shared
      void loadRow();
    }
  };
  const { call } = bench;
  // listed by the main server and not on this machine: made on a machine
  // whose home this one is not, or taken off it — said as that, never the
  // machine's words (byj-1wv.115.48)
  const [missing, setMissing] = useState(false);
  const loadInfo = useCallback(async () => {
    try {
      setInfo(await call<BuildInfo>("builder/state", { name }));
      setError(null);
      setMissing(false);
    } catch (err) {
      if (err instanceof RpcError && err.code === Codes.notFound) setMissing(true);
      else setError(errText(err));
    }
  }, [call, name]);
  useEffect(() => {
    void loadRow();
  }, [loadRow]);
  useEffect(() => {
    if (bench.ready) void loadInfo();
  }, [bench.ready, loadInfo]);
  // what the machine says of its share is the truth: the main server's
  // record is set to it where they differ — a report that failed, a share
  // stopped elsewhere, or one the migration took as shared — so /agents and
  // the page with the machine off say it rightly (byj-1wv.115.49)
  // Once, as the page opens and has read both: never after what the person
  // does on it, whose own report it is, nor again on a poll's read, so a
  // write is never made from what the machine said before an action, nor
  // made again and again where the main server refuses it.
  const synced = useRef(false);
  const machineShare = info?.share.share;
  const machineOn = info?.share.on === true;
  useEffect(() => {
    if (synced.current || !info || !rowRead || rowProblem) return;
    synced.current = true;
    // none kept (its first report failed), or one that says otherwise
    if (!machineShare || (row && row.share_id === machineShare && row.shared === machineOn)) return;
    api.putAgent(name, { share_id: machineShare, shared: machineOn }).then(
      () => void loadRow(),
      () => undefined,
    );
  }, [info, machineShare, machineOn, row, rowRead, rowProblem, name, loadRow]);
  // the builder answered: what it drafted is read again
  useEffect(() => {
    if (!builder.thread.busy && builder.thread.items.some((x) => x.kind === "agent")) void loadInfo();
  }, [builder.thread.busy]); // eslint-disable-line react-hooks/exhaustive-deps
  // shared, the machine on and the share not answering yet: it is reopening
  // as the machine starts (a few seconds) — asked again until it answers,
  // wherever the page is, and never said to be off while the machine runs
  // (byj-1wv.115.38); one not answering after reopenWaitMs is said so, and
  // asked after less often, since it may yet
  const on = info?.share.on === true;
  const answering = !!row && agentState(row) === "live";
  const [slow, setSlow] = useState(false);
  const notAnswering = on && !answering && bench.ready;
  useEffect(() => {
    if (!notAnswering) setSlow(false);
  }, [notAnswering]);
  useEffect(() => {
    if (!notAnswering) return;
    const started = Date.now();
    const id = setInterval(
      () => {
        if (!slow && Date.now() - started >= reopenWaitMs) setSlow(true);
        else void loadRow();
      },
      slow ? reopenSlowPollMs : reopenPollMs,
    );
    return () => clearInterval(id);
  }, [notAnswering, slow, loadRow]);
  // how its share stands, said alike in the bar and on Share
  const sharedWords = on
    ? answering
      ? model.model?.credential === "subscription" ? t("agents:sharedOwnerOnly") : t("agents:sharedLive")
      : notAnswering ? (slow ? t("agents:sharedNotAnswering") : t("agents:sharedReopening")) : t("agents:sharedOff")
    : t("agents:notShared");
  // the machine off: what the main server keeps, usable without it (byj-1wv.115.49)
  if (machine === null) return rowRead ? <MachineOff name={name} row={row} problem={rowProblem} onRetry={() => void loadRow()} /> : <PageLoading />;
  if (missing)
    return (
      <PageNotice message={t("agents:notOnMachine", { name })}>
        <Link to="/agents">{t("agents:backToAgents")}</Link>
      </PageNotice>
    );
  if (error && !info) return <PageNotice error={error} />;
  if (!info) return <PageLoading />;
  // what is kept says what is done: drafted, abilities ticked, every example
  // passing (not a run that failed some), shared; files may be left out
  const last = info.record["last-test"];
  const drafted = !!info.spec.description;
  const tested = allPassed(info);
  // how it stands, in the bar under the head as the app's pages say theirs:
  // its share, then the model it runs on and its last test
  const credential = model.model?.credential;
  const facts = [
    credential ? t(`agents:modelIs.${credential}`) : model.problem ? t("agents:modelUnread") : null,
    last ? t("agents:testSummary", { passed: last.passed, of: last.of }) : t("agents:notTested"),
  ].filter(Boolean);
  const askModel = credential === "none" || asked;
  // a copy for teammates once drafted; Delete while the machine answers
  const headItems = [
    ...(drafted ? [{ label: t("agents:teammatesTitle"), onSelect: () => setTeammates(true) }] : []),
    ...(bench.ready ? [{ label: t("agents:delete"), danger: true, onSelect: () => void remove() }] : []),
  ];
  const modelFirst = (title: string) => (
    <ModelFirst
      title={title}
      terminal={machine?.share_id}
      opening={model.opening}
      waiting={model.waiting}
      problem={model.problem}
      onSignIn={() => void model.signIn()}
      onKey={() => setKeying("first")}
      onDone={
        asked && credential !== "none"
          ? () => {
              setAsked(false);
              model.stopWaiting();
            }
          : undefined
      }
    />
  );
  // what it takes to share it is a draft alone: an agent with no ability
  // answers from what it is told, one with no file from what it knows, one
  // never tested answers all the same — each said as what it is
  const checks: Check[] = [
    { key: "draft", label: t("agents:check.draft"), need: "required", done: true, onGo: () => go("agent", "agent-builder") },
    { key: "examples", label: t("agents:check.examples"), need: "recommended", done: tested, onGo: () => go("test", "agent-examples") },
    { key: "share", label: t("agents:check.share"), need: "required", done: on, onGo: () => go("share") },
  ];
  return (
    <Page className="agent-page">
      <PageHead
        title={name}
        actions={headItems.length > 0 && <RowMenu label={t("agents:more")} items={headItems} />}
      />
      {deleteProblem && <p className="error">{t("common:error", { m: deleteProblem })}</p>}
      <StateBar
        className="agent-bar"
        label={t("agents:barLabel")}
        state={on ? (answering ? "live" : "paused") : "ended"}
        stateLabel={<span className="share-bar-state">{sharedWords}</span>}
        actions={row?.share_id && on && <Link to={`/s/${encodeURIComponent(row.share_id)}`}>{t("agents:openSharePage")}</Link>}
        testid="agent-bar"
      >
        <span className="share-bar-facts">{facts.join(" · ")}</span>
      </StateBar>
      {/* the workbench lost, said as the workbench view says it, never every control gone quiet (byj-1wv.115.48) */}
      {bench.ended === "taken" && (
        <p className="banner" data-testid="bench-taken">
          {t("workbench:taken")}{" "}
          <button className="small" onClick={bench.retake}>
            {t("workbench:useHere")}
          </button>
        </p>
      )}
      {bench.ended === "forbidden" && <p className="banner">{t("workbench:forbidden")}</p>}
      {bench.ended === "gone" && <p className="banner">{t("workbench:gone404")}</p>}
      {bench.waiting && <p className="banner muted">{t("workbench:waitingMachine")}</p>}
      {!drafted ? (
        // what must come first, in order and nothing else: the model, then
        // what the agent is for, which it is drafted from
        <ol className="builder-start" data-testid="start">
          <li>
            {askModel ? (
              modelFirst(t("agents:start.model"))
            ) : (
              <Card
                title={t("agents:start.model")}
                testid="start-model"
                tools={
                  <button className="small" aria-haspopup="dialog" aria-controls="api-key-first" onClick={() => setKeying("first")}>
                    {credential === "key" ? t("agents:replaceKey") : t("agents:addKey")}
                  </button>
                }
              >
                {credential ? (
                  <p className="flush">
                    <span className="step-done" aria-hidden="true">
                      ✓{" "}
                    </span>
                    {t(`agents:modelIs.${credential}`)}
                  </p>
                ) : model.problem ? (
                  // the read failed: said, and asked again on a press, never a wait that does not end
                  <div className="row wrap">
                    <p className="error grow flush">{t("common:error", { m: model.problem })}</p>
                    <button className="small" onClick={() => void model.load()}>
                      {t("common:retry")}
                    </button>
                  </div>
                ) : (
                  <p className="muted flush">{t("agents:modelReading")}</p>
                )}
              </Card>
            )}
          </li>
          <li>
            <Builder
              info={info}
              th={builder}
              bench={bench}
              title={t("agents:start.describe")}
              waitFor={credential === "none" ? t("agents:start.describeWaits") : undefined}
              onSignIn={toModel}
            />
          </li>
        </ol>
      ) : (
        <>
          {/* what it is for, as drafted */}
          <PageLead>
            <p>{info.spec.description}</p>
          </PageLead>
          {askModel && modelFirst(t("agents:modelFirstTitle"))}
          {!on && <Checklist items={checks} />}
          <Tabs prefix="agent" label={t("agents:tabsLabel")} value={tab} onChange={setTab} tabs={tabs.map((x) => ({ id: x, label: t(`agents:tab.${x}`) }))} />
          {/* each tab a guide, the full width; every tab opened kept, the others hidden: what was typed or opened on one is there on coming back */}
          <section role="tabpanel" id={panelId("agent", "agent")} aria-labelledby={tabId("agent", "agent")} className="tab-panel" data-testid="tab-agent" hidden={tab !== "agent"}>
            <Guide testid="guide-agent" title={t("agents:guide.agent.title")} lead={t("agents:guide.agent.lead")} tips={[t("agents:guide.agent.tip1"), t("agents:guide.agent.tip2"), t("agents:guide.agent.tip3")]}>
              <InstructionStep info={info} />
              <Abilities info={info} bench={bench} onInfo={setInfo} />
              <Knowledge info={info} bench={bench} />
            </Guide>
            {/* a change asked of the builder, beside the guide and not one of its steps: nothing waits on it */}
            <div className="builder-aside">
              <Builder info={info} th={builder} bench={bench} title={t("agents:changeTitle")} onSignIn={toModel} />
            </div>
          </section>
          <section role="tabpanel" id={panelId("agent", "test")} aria-labelledby={tabId("agent", "test")} className="tab-panel" data-testid="tab-test" hidden={tab !== "test"}>
            {kept("test") && (
              <Guide testid="guide-test" title={t("agents:guide.test.title")} lead={t("agents:guide.test.lead")} tips={[t("agents:guide.test.tip1"), t("agents:guide.test.tip2")]}>
                <TryStep info={info} th={trying} bench={bench} onSignIn={toModel} />
                <Examples info={info} bench={bench} onInfo={setInfo} onSignIn={toModel} share={row?.share_id ?? info.share.share} />
              </Guide>
            )}
          </section>
          <section role="tabpanel" id={panelId("agent", "share")} aria-labelledby={tabId("agent", "share")} className="tab-panel" data-testid="tab-share" hidden={tab !== "share"}>
            {kept("share") && (
              <Guide testid="guide-share" title={t("agents:guide.share.title")} lead={t("agents:shareLead")} tips={[t("agents:guide.share.tip1"), t("agents:guide.share.tip2")]}>
                <Share
                  info={info}
                  bench={bench}
                  row={row}
                  model={model.model}
                  modelProblem={model.problem}
                  stateWords={sharedWords}
                  onKey={() => setKeying("share")}
                  onInfo={loadInfo}
                  onRow={loadRow}
                />
              </Guide>
            )}
          </section>
          <Teammates info={info} bench={bench} model={model.model} open={teammates} onClose={() => setTeammates(false)} />
        </>
      )}
      {keying && (
        <FormDialog
          id={`api-key-${keying}`}
          title={t("agents:addKey")}
          submitLabel={t("agents:keep")}
          canSubmit={key.trim().length > 0}
          onClose={() => setKeying(null)}
          onSubmit={async () => {
            model.kept(await bench.call<ModelState>("builder/key", { key: key.trim() }));
            setKey("");
            setKeying(null);
            setAsked(false);
          }}
        >
          <p className="muted small">{t("agents:keyHow")}</p>
          <label className="stack">
            <span>{t("agents:apiKey")}</span>
            <input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} autoFocus />
          </label>
        </FormDialog>
      )}
      {confirmDialog}
    </Page>
  );
}
